import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, X } from 'lucide-react';
import { api } from '../lib/api';
import HRLogo, { type HRLogoState } from './HRLogo';

interface AIChatProps {
  agentName: string;
  agentPersonality: string;
  replicaId?: string;
  onClose?: () => void;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/**
 * AIChat Component
 *
 * Provides a chat interface connected to the backend /api/v1/chat endpoint.
 * Sends messages to the replica and displays streamed or batch replies.
 */
const AIChat: React.FC<AIChatProps> = ({ agentName, replicaId, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Logo lifecycle: listening (focused) → thinking (loading) → default
  const logoState: HRLogoState = isLoading ? 'thinking' : isFocused ? 'listening' : 'default';

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = async () => {
    const content = input.trim();
    if (!content || isLoading || !replicaId) return;

    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history from recent messages
      const conversationHistory = messages.slice(-20).map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await api.post<{ reply: string; auditId: string }>('/chat', {
        replicaId,
        message: content,
        conversationHistory,
      });

      if (res.data?.reply) {
        const assistantMsg: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: res.data.reply,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMsg]);
      } else if (res.error) {
        const errorMsg: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `⚠️ ${res.error}`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMsg]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      const errorMsg: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '⚠️ Failed to reach the server. Please check if the backend is running.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-[500px] bg-hig-bg-elevated rounded-2xl shadow-xl border border-hig-divider overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-hig-divider bg-hig-bg-secondary/50">
        <div className="flex items-center gap-3">
          <HRLogo
            state={logoState}
            size={40}
            aria-label={isLoading ? 'Thinking' : isFocused ? 'Listening' : 'Online'}
          />
          <div>
            <h3 className="font-semibold text-sm text-hig-text-primary">{agentName}</h3>
            <p className="text-xs text-hig-text-tertiary">
              {isLoading ? 'Thinking…' : isFocused ? 'Listening…' : 'Online'}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary transition-colors"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <HRLogo state="default" size={40} className="opacity-40 mb-3" />
            {replicaId ? (
              <>
                <p className="text-sm text-hig-text-secondary font-medium">
                  Start a conversation with {agentName}
                </p>
                <p className="text-xs text-hig-text-tertiary mt-1 max-w-xs">
                  Ask questions, give instructions, or test how your Replica responds
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-hig-text-secondary font-medium">
                  Chat Preview
                </p>
                <p className="text-xs text-hig-text-tertiary mt-1 max-w-xs">
                  Create and deploy a Replica to start chatting
                </p>
              </>
            )}
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${msg.role === 'user'
                ? 'bg-hig-accent text-white rounded-br-md'
                : 'bg-hig-bg-tertiary text-hig-text-primary rounded-bl-md'
                }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>
              <p
                className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/50' : 'text-hig-text-tertiary'
                  }`}
              >
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-hig-bg-tertiary rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-hig-accent" />
                <span className="text-xs text-hig-text-tertiary">Generating response…</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-hig-divider bg-hig-bg-secondary/30">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={`Message ${agentName}…`}
            className="flex-1 px-4 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-sm text-hig-text-primary placeholder:text-hig-text-tertiary focus:outline-none focus:border-hig-accent transition-colors"
            disabled={isLoading || !replicaId}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="p-2.5 bg-hig-accent text-white rounded-xl hover:bg-hig-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;